import { Box, Center, Group, Pagination, Stack, Table, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'

interface Props {
  children: React.ReactNode
  columns: React.ReactNode
  isEmpty?: boolean
  emptyLabel?: string
  page?: number
  pageSize?: number
  total?: number
  onPageChange?: (page: number) => void
}

export function DataTable({
  children,
  columns,
  isEmpty,
  emptyLabel,
  page = 1,
  pageSize = 15,
  total = 0,
  onPageChange,
}: Props) {
  const { t } = useTranslation()
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const firstRow = total === 0 ? 0 : (page - 1) * pageSize + 1
  const lastRow = Math.min(page * pageSize, total)

  return (
    <Stack gap={0}>
      <Box style={{ overflowX: 'auto', width: '100%' }}>
        <Table highlightOnHover verticalSpacing="md" miw={720}>
          <Table.Thead>
            <Table.Tr>{columns}</Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isEmpty ? (
              <Table.Tr>
                <Table.Td colSpan={100}>
                  <Center py="xl">
                    <Text c="dimmed" size="sm">
                      {emptyLabel ?? t('component.table.empty')}
                    </Text>
                  </Center>
                </Table.Td>
              </Table.Tr>
            ) : (
              children
            )}
          </Table.Tbody>
        </Table>
      </Box>

      {total > 0 && totalPages > 1 && onPageChange && (
        <Group justify="space-between" align="center" mt="md" px="xs">
          <Text size="sm" c="dimmed">
            {t('component.table.showing', { first: firstRow, last: lastRow, total })}
          </Text>
          <Pagination
            value={page}
            onChange={onPageChange}
            total={totalPages}
            size="sm"
            color="laut"
            radius="md"
            withEdges
          />
        </Group>
      )}
    </Stack>
  )
}
